import React, { Fragment, useEffect } from 'react';
import { useRouter } from 'next/router';
import { styled } from '@linaria/react';
import { css } from '@linaria/core';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { Snackbar, zIndex } from '@spaceshipapp/cambridge';
import Link from '../../components/Link';
import useMessageHandler from '../../hooks/useMessageHandler';
import * as analytics from '../../analytics/ui';
import { ErrorMessage } from '../../contexts/message';

const getErrorMessage = ({ message, code }: ErrorMessage): string | null => {
  const errorCode = code ? code.toString() : '';

  if (message === 'Network Error' || message === 'Request aborted') {
    return 'errors:General.Network';
  }

  if (message === 'Request failed with status code 401') {
    return 'errors:General.401';
  }

  if (message === 'Request failed with status code 429') {
    return 'errors:General.429';
  }

  if (!errorCode) return message;

  switch (errorCode) {
    //Custom coupon errors
    case 'coupon_apply_error':
      return null;
    case 'coupon_checkout_error':
      return 'common:Coupon.cardTypeSpecificCouponInvalid';
    // Auth - Login, Register, Reset Password
    case '1040':
    case '1050':
    case '1110':
      return 'errors:Login.IncorrectCredentials';
    case '1120':
      return 'errors:Register.AccountExisted';
    case '1131':
      return 'errors:ResetPassword.NoExistingAccount';
    // Create shipment
    case '4030':
    case 'api-1203':
    case 'api-1513':
    case 'api-5028':
    case 'api-5145':
    case 'api-ERR35':
    case 'api-ERR37':
    case 'api-ERR38':
    case 'api-9510121':
    case 'api-9510114':
    case 'api-9510111':
    case 'api-9510113':
    case 'api-247':
    case 'api-692':
      return 'errors:PickupOrDropoff.OverCutoffTime';
    case 'api-ERR18':
    case 'api-5056':
      return 'errors:PickupOrDropoff.DistantPickup';
    // Add sender
    case 'api-534':
    case 'api-548':
      return 'errors:PickupOrDropoff.InvalidPostalCode';
    // Promo code
    case '2010':
    case '2012':
      return 'errors:Coupon.InvalidPromotion';
    case '2011':
      return 'errors:Coupon.Duplicate';
    // Any event
    case '2020':
    case '2022':
    case '2030':
    case '2040':
      return 'errors:General.SystemError';
    default: {
      return `errors:LegacyApi.${errorCode}`;
    }
  }
};

const Alert: React.FC = () => {
  const router = useRouter();
  const { t } = useTranslation('common');
  const { error, success, resetAll } = useMessageHandler();

  const mappedMsg = success || (error ? getErrorMessage(error) : null);

  useEffect(() => {
    if (mappedMsg) {
      analytics.sendAlertShown(router.asPath, mappedMsg, error);
    }
  }, [mappedMsg]);

  useEffect(() => {
    const handleRouteChange = (_: string, { shallow }: { shallow: boolean }) => {
      // useSlug() in create shipment page will trigger a route change, ignore it.
      if (!shallow) resetAll();
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, []);

  const getSupportLink = (error?: ErrorMessage | null): string => {
    const { code } = error || {};
    if (!code) return '';

    switch (code) {
      case '2010':
      case '2011':
      case '2012':
        return t('URL.promotions');
      default:
        return '';
    }
  };

  return mappedMsg ? (
    <Stack>
      <Snackbar level={success ? 'success' : 'error'} onClose={resetAll}>
        <Trans
          i18nKey={mappedMsg}
          fallback={error?.message}
          /* eslint-disable react/jsx-key */
          components={[<Fragment />, <Link href={getSupportLink(error)} className={linkStyle} />]}
          /* eslint-enable react/jsx-key */
          values={{ card: error?.message ?? 'credit card' }}
        />
      </Snackbar>
    </Stack>
  ) : null;
};

const Stack = styled.div`
  position: fixed;
  top: 6.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: ${zIndex.ALERT};
  padding: 0 1rem;
  min-width: min(100%, 480px);
`;

const linkStyle = css`
  white-space: pre;
`;

export default Alert;
